import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Pencil, Trash, FloppyDisk, X, Phone, EnvelopeSimple, MapPin, Globe, Wrench, Buildings, WhatsappLogo } from "@phosphor-icons/react";

const blank = { name: "", contact_name: "", phone: "", email: "", address: "", specialties: [], contract_notes: "", website: "" };

function waPhone(raw) {
  if (!raw) return null;
  const digits = String(raw).replace(/[^\d]/g, "");
  return digits.length >= 8 ? digits : null;
}

export default function MaintenanceCompanies() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: companies = [] } = useQuery({ queryKey: ["maintenance-companies"], queryFn: () => http.get("/maintenance-companies").then(r => r.data) });
  const { data: equipment = [] } = useQuery({ queryKey: ["equipment"], queryFn: () => http.get("/equipment").then(r => r.data) });

  const canManage = ["admin", "general_manager", "maintenance_manager"].includes(user?.role);

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");

  const open = (mode, data = blank) => { setForm({ ...blank, ...data, specialties: data.specialties || [] }); setModal({ mode, id: data.id }); setErr(""); };
  const close = () => setModal(null);

  // unique categories pulled from equipment
  const allCategories = Array.from(new Set(equipment.map(e => e.category).filter(Boolean))).sort();

  const toggleSpec = (c) => setForm(f => ({ ...f, specialties: f.specialties.includes(c) ? f.specialties.filter(x => x !== c) : [...f.specialties, c] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (modal.mode === "create") await http.post("/maintenance-companies", form);
      else await http.patch(`/maintenance-companies/${modal.id}`, form);
      qc.invalidateQueries({ queryKey: ["maintenance-companies"] });
      close();
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Soft-delete maintenance company "${name}"? Equipment linked to it will be unlinked.`)) return;
    try { await http.delete(`/maintenance-companies/${id}`); qc.invalidateQueries({ queryKey: ["maintenance-companies"] }); qc.invalidateQueries({ queryKey: ["equipment"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Service partners</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1 flex items-center gap-3"><Buildings size={26} /> Maintenance companies</h1>
          <p className="text-sm text-slate-600 mt-1">Third-party service partners. Each equipment can optionally be linked to one for service contracts and quick contact lookup.</p>
        </div>
        {canManage && (
          <button onClick={() => open("create")} data-testid="mc-new-button" className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
            <Plus size={14} weight="bold" /> Add company
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {companies.map(c => (
          <div key={c.id} data-testid={`mc-card-${c.id}`} className="rmms-card p-5 rmms-card-hover">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-base">{c.name}</div>
                {c.contact_name && <div className="text-xs text-slate-500 mt-0.5">Contact: {c.contact_name}</div>}
              </div>
              <div className="flex gap-1">
                {canManage && <button onClick={() => open("edit", c)} data-testid={`mc-edit-${c.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>}
                {canManage && <button onClick={() => remove(c.id, c.name)} data-testid={`mc-delete-${c.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 text-[12px] text-slate-600">
              {c.phone && (
                <div className="flex items-center gap-1.5">
                  <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-[#002FA7]" data-testid={`mc-call-${c.id}`}>
                    <Phone size={11} /> <span className="font-mono-data">{c.phone}</span>
                  </a>
                  {waPhone(c.phone) && (
                    <a
                      href={`https://wa.me/${waPhone(c.phone)}?text=${encodeURIComponent(`Hi${c.contact_name ? " " + c.contact_name.split(" ")[0] : ""}, this is regarding maintenance services. `)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`mc-whatsapp-${c.id}`}
                      title="WhatsApp"
                      className="inline-flex items-center justify-center h-4 w-4 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                    >
                      <WhatsappLogo size={10} weight="fill" />
                    </a>
                  )}
                </div>
              )}
              {c.email && <div className="flex items-center gap-1.5"><EnvelopeSimple size={11} /> <a href={`mailto:${c.email}`} className="font-mono-data truncate hover:text-[#002FA7]" data-testid={`mc-email-${c.id}`}>{c.email}</a></div>}
              {c.address && <div className="flex items-center gap-1.5"><MapPin size={11} /> {c.address}</div>}
              {c.website && <div className="flex items-center gap-1.5"><Globe size={11} /> <a href={`https://${c.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline">{c.website}</a></div>}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex flex-wrap gap-1">
                {(c.specialties || []).map(s => <span key={s} className="text-[10px] px-1.5 py-0.5 border border-slate-200 rounded-sm font-mono-data text-slate-600">{s}</span>)}
              </div>
              <div className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Wrench size={10} /> <span className="font-mono-data tabular-nums">{c.equipment_count || 0}</span> equipment</div>
            </div>
            {c.contract_notes && <div className="mt-2 text-[11px] text-slate-500 italic">{c.contract_notes}</div>}
          </div>
        ))}
        {companies.length === 0 && <div className="col-span-full py-12 text-center text-sm text-slate-500" data-testid="mc-empty">No maintenance companies yet.</div>}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="mc-modal">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div><div className="overline">{modal.mode === "create" ? "New" : "Edit"}</div><h2 className="text-xl font-heading font-semibold tracking-tight mt-1">Maintenance company</h2></div>
              <button type="button" onClick={close} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div>
              <label className="overline mb-1.5 block">Company name *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="mc-name-input" className="rmms-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="overline mb-1.5 block">Contact name</label><input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} data-testid="mc-contact-input" className="rmms-input" /></div>
              <div><label className="overline mb-1.5 block">Phone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="mc-phone-input" className="rmms-input font-mono-data" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="overline mb-1.5 block">Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="mc-email-input" className="rmms-input font-mono-data" /></div>
              <div><label className="overline mb-1.5 block">Website</label><input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} data-testid="mc-website-input" className="rmms-input font-mono-data" /></div>
            </div>
            <div><label className="overline mb-1.5 block">Address</label><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="mc-address-input" className="rmms-input" /></div>
            <div>
              <label className="overline mb-1.5 block">Specialties (equipment categories)</label>
              <div className="flex flex-wrap gap-1 p-2 border border-slate-200 rounded-sm">
                {allCategories.length === 0 && <span className="text-xs text-slate-400 px-1">No equipment categories defined yet.</span>}
                {allCategories.map(c => (
                  <button key={c} type="button" onClick={() => toggleSpec(c)} data-testid={`mc-spec-${c}`}
                    className={`text-[11px] px-2 py-1 border rounded-sm transition-colors ${
                      form.specialties.includes(c) ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-200 hover:bg-slate-50"
                    }`}>{c}</button>
                ))}
              </div>
            </div>
            <div><label className="overline mb-1.5 block">Contract notes</label><textarea rows={2} value={form.contract_notes} onChange={e => setForm({ ...form, contract_notes: e.target.value })} data-testid="mc-notes-input" className="w-full rounded-md border border-slate-200 p-3 text-sm outline-none focus:border-[#002FA7]" /></div>
            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={close} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="mc-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
